import { getPageType } from '~/events/utils/pageType'
import eventBus from '~/events/eventBus'
import eventDefinitions from '~/events/eventDefinitions'
import productTransformer from './transformers/product.transformer'

eventBus.$on(eventDefinitions.CHECKOUT.STEP_OPTION, handleCheckoutStep)

function handleCheckoutStep({ number, option, cart }) {
  if (!cart || !cart.cartData || !Array.isArray(cart.cartData.items)) {
    return
  }

  const transformedItems = cart.cartData.items.map(transformCartItem).filter(Boolean)

  if (transformedItems.length === 0) {
    return
  }

  const { totalValue, totalValueVat, mktValAll } = calculateCartTotals(transformedItems)
  const shippingFields = getShippingFields(option, cart.input)
  const paymentFields = getPaymentFields(option, cart.input)
  const customerInfoFields = getCustomerInfoFields(option, cart.cartData, cart.input)

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({
    event: option,
    ecommerce: {
      currency: cart.cartData.currencyCode,
      value: totalValue,
      value_vat: totalValueVat,
      coupon: '',
      mkt_val_all: mktValAll,
      ...shippingFields,
      ...paymentFields,
      items: transformedItems,
      google_tag_params: {
        ecomm_pagetype: getPageType(),
        ecomm_prodid: transformedItems.map(item => String(item.item_id)),
        ecomm_totalvalue: totalValue,
        ecomm_totalvalue_all: transformedItems.map(item => item.price * item.quantity),
      },
      ...customerInfoFields,
    },
  })
}

function transformCartItem(item, i) {
  if (!item) {
    return null
  }

  const custom = item.attributes?.custom || {}
  const product = item.product || createProductFallback(item)
  const orderItem = createOrderItemFallback(item, product)

  return productTransformer(
    product,
    item.amount,
    item.prices?.unitWithoutTax,
    item.prices?.unitWithTax,
    i,
    orderItem,
    custom.colour ?? null,
    custom.master_item_id ?? null,
  )
}

function createProductFallback(item) {
  const custom = item.attributes?.custom || {}
  const itemName =
    item.name || item.fullname || item.productName || item.variantName || item.title || 'Položka v košíku'
  const productId =
    item.productId ||
    item.shoppable?.productId ||
    item.variant?.productId ||
    custom.master_item_id ||
    custom.externalId ||
    item.id

  return {
    id: item.variantId || item.shoppableId || item.shoppable?.id || item.variant?.id || item.id || productId,
    productId,
    name: itemName,
    fullname: itemName,
    categoryFullname: item.categoryFullname || item.product?.categoryFullname || '',
    categoryName: item.categoryName || item.product?.categoryName || '',
    attributes: {
      ...(item.product?.attributes || {}),
      width: custom.width || item.product?.attributes?.width,
      height: custom.height || item.product?.attributes?.height,
      cms_ids: custom.master_item_id ? [custom.master_item_id] : item.product?.attributes?.cms_ids || [],
    },
    availability: item.availability || item.shoppable?.availability || item.variant?.availability || {},
    prices: item.prices || {},
  }
}

function createOrderItemFallback(item, product) {
  const custom = item.attributes?.custom || {}

  return {
    ...item,
    attributes: {
      ...(item.attributes || {}),
      custom: {
        ...custom,
        externalId: custom.externalId || item.productId || product.productId || item.id || product.id || '',
        width: custom.width || product.attributes?.width || '',
        height: custom.height || product.attributes?.height || '',
      },
    },
  }
}

/**
 * Calculates cart total values.
 */
function calculateCartTotals(items) {
  return items.reduce(
    (totals, item) => {
      totals.totalValue += item.price * item.quantity
      totals.totalValueVat += item.price_vat * item.quantity
      totals.mktValAll += item.price * item.quantity
      return totals
    },
    { totalValue: 0, totalValueVat: 0, mktValAll: 0 },
  )
}

/**
 * Extracts shipping fields based on the checkout option.
 */
function getShippingFields(option, input = {}) {
  return ['add_shipping_info', 'add_payment_info', 'add_customer_info'].includes(option)
    ? {
        shipping_tier: input.shipper?.name || 'Unknown',
        shipping: input.shipper?.prices?.totalWithoutTax || 0.0,
        shipping_vat: input.shipper?.prices?.totalWithTax || 0.0,
      }
    : {}
}

/**
 * Extracts payment fields if the option is 'add_customer_info'.
 */
function getPaymentFields(option, input = {}) {
  return ['add_payment_info', 'add_customer_info'].includes(option)
    ? {
        payment_type: input.payment?.name || 'Unknown',
        payment_fee: input.payment?.prices?.totalWithTax || 0.0,
      }
    : {}
}

/**
 * Splits full name into first and last names.
 */
function splitFullName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(' ')
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
  }
}

/**
 * Splits street and house number from a billing address.
 */
function splitStreetAndNumber(billingStreet) {
  const normalizedStreet = String(billingStreet || '').trim()
  const parts = normalizedStreet.split(' ')
  return {
    street: parts.slice(0, -1).join(' ') || normalizedStreet,
    no: parts.slice(-1)[0] || '',
  }
}

/**
 * Extracts customer info fields based on the checkout option.
 */
function getCustomerInfoFields(option, cartData, input = {}) {
  if (option !== 'add_customer_info') return {}

  const { firstName, lastName } = splitFullName(input.fullName)
  const { street, no } = splitStreetAndNumber(input.billingStreet)

  return {
    customer_info: {
      sign_up: input.isRegistration,
      customer_type: input.isBusiness ? 'B2B' : 'B2C',
      email: input.email,
      phone: input.phone ? input.phone.replace(/\s+/g, '') : '',
      address: {
        name: firstName,
        surname: lastName,
        street,
        no,
        city: input.billingCity,
        region: '',
        country: cartData.billingAddress?.countryAlpha2 || input.billingCountry || input.countryCode || '',
        zip: input.billingPostcode,
      },
    },
  }
}
